// Observation-lifecycle transition graph (ADR-0105, ADR-0129).
//
// Mirror of `delegation.ts` in shape, deliberately NOT in signature:
// this vocabulary is recipe-declared (`recipe.observationStatuses`),
// so every function here takes a `Recipe` — the in-code marker of
// which lifecycle is engine-owned (delegation) and which is
// recipe-declared (observation status). See delegation.ts's own
// docstring on `validateDelegationTransition` for the other half of
// that asymmetry.
//
// The security model (council 2026-08-31, ADR-0129):
//   - The node ceiling (`settableBy`, ADR-0070 level 2) stays the
//     PRIMARY gate and is always enforced, via
//     `resolveObservationStatus` — the single choke point every caller
//     already derives `terminal` from.
//   - When the vocabulary is GRAPH-ENABLED (any status declares an
//     edge), the edge set narrows further: a transition must also be a
//     declared edge whose `actors` include the caller's lane. The
//     effective permission is the intersection — the edge never widens
//     what the ceiling allows.
//   - Fallback for findings not yet in the graph: a `from` of null
//     (no lifecycle entry yet), an unknown legacy value, or a
//     `retired` status has no live edges by construction — those
//     findings re-enter the graph under the node ceiling alone, i.e.
//     exactly the pre-graph behavior. Once a finding sits on a live
//     status, edges govern. This is what keeps the ADR-0105 vocabulary
//     replacement deployable with zero data migration: persisted rows
//     on retired keys are not frozen, they re-enter.
//
// Flat vocabularies (no edges anywhere) behave byte-for-byte as
// before this module existed: ceiling only.
import { ConflictError, ValidationError } from "@bitacorapro/shared";
import { closureLanes, } from "./recipe.js";
import { DERIVED_SEVERITIES, defaultPlazoDiasFor } from "./taxonomy.js";
import { resolveObservationStatus } from "./invariants.js";
/** True when at least one status in the vocabulary declares an edge —
 *  the switch that turns graph enforcement on for a recipe
 *  (ADR-0105 Decision 1: default [] preserves flat vocabularies). */
export function isObservationGraphEnabled(recipe) {
    return recipe.observationStatuses.some((s) => s.transitionsTo.length > 0);
}
/**
 * Enforce one observation-status transition. Returns the resolved
 * TARGET entry so the caller stamps `finding_events.to_status_terminal`
 * from the same call that authorized the write — same single-choke-point
 * contract `resolveObservationStatus` documents.
 *
 * Throws:
 *   - ValidationError (via resolveObservationStatus) when the target is
 *     unknown to the vocabulary or the actor is outside its ceiling —
 *     including every retired status, whose ceiling is [].
 *   - ConflictError INVALID_OBSERVATION_TRANSITION when the vocabulary
 *     is graph-enabled, the origin is a live status, and no declared
 *     edge covers (from → to, actor).
 *   - ValidationError OBSERVATION_NOTE_REQUIRED when the target declares
 *     `requiresNote` and `note` is absent or blank. Checked LAST, on the
 *     node, on every lane (flat, fallback, edge): an illegal transition
 *     reports as illegal, never as "missing note", and a NULL/retired
 *     origin — which skips the edge check — still cannot reach a
 *     note-requiring status silently.
 *   - ConflictError INVALID_OBSERVATION_TRANSITION when a CLOSURE lane
 *     (`closureLanes`) tries to reach `observationClosure.fromStatus`
 *     from an origin outside the live graph (NULL, unknown, retired).
 *     The ceiling-only fallback would otherwise let it skip every edge
 *     into the status it then closes from — a self-certified closure
 *     with no recorded resolution (ADR-0134). Postventa's closure lane
 *     (the propietario) is outside the `resuelta` ceiling anyway.
 *   - ValidationError OBSERVATION_EVIDENCE_REQUIRED when the target
 *     declares `requiresEvidence` and `evidenceCount` is absent or < 1.
 *     Checked last with the note, on every lane, same reasoning.
 */
export function validateObservationTransition(input) {
    const { recipe, from, to, actor } = input;
    // Node ceiling first — ADR-0070 level 2, always on, every vertical.
    const target = resolveObservationStatus(recipe, to, actor);
    if (isObservationGraphEnabled(recipe)) {
        const origin = from === null ? undefined : recipe.observationStatuses.find((s) => s.key === from);
        // Fallback lane: null / legacy-unknown / retired origins re-enter the
        // graph under the ceiling alone (see module docstring). A live
        // origin must own a declared edge for (to, actor).
        if (origin !== undefined && !origin.retired) {
            const edge = origin.transitionsTo.find((t) => t.to === to && t.actors.includes(actor));
            if (!edge) {
                throw new ConflictError({
                    code: "INVALID_OBSERVATION_TRANSITION",
                    message: `Cannot move an observation from "${from}" to "${to}" as actor "${actor}"`,
                });
            }
            // ADR-0141 — a note demanded by the EDGE (see the schema comment on
            // observationTransitionSchema.requiresNote): checked here, where the
            // edge is known, with the same error the node-level rule reports so
            // callers don't branch on which of the two declared it.
            if (edge.requiresNote)
                assertNote(target.key, input.note);
        }
        else if (to === recipe.observationClosure?.fromStatus &&
            closureLanes(recipe).includes(actor)) {
            throw new ConflictError({
                code: "INVALID_OBSERVATION_TRANSITION",
                message: `A closing actor cannot reach "${to}" from outside the live graph ("${from ?? "sin estado"}")`,
            });
        }
    }
    assertNoteRequirement(target, input.note);
    assertEvidenceRequirement(target, input.evidenceCount);
    return target;
}
function assertNoteRequirement(target, note) {
    if (!target.requiresNote)
        return;
    assertNote(target.key, note);
}
function assertNote(targetKey, note) {
    if (typeof note === "string" && note.trim().length > 0)
        return;
    throw new ValidationError({
        code: "OBSERVATION_NOTE_REQUIRED",
        message: `A note is required to move an observation to "${targetKey}"`,
        details: { note: [`status "${targetKey}" requires a note`] },
    });
}
/**
 * The statuses `from` may legally move to, for this actor — the list a
 * UI renders as its per-state action set, and the list the public
 * share-link guards resolve their target from (they must NEVER filter
 * the whole vocabulary by settableBy again: with edges, "what can this
 * link do *from here*" is the only well-formed question — the old
 * whole-vocabulary filter is exactly what broke as soon as a second
 * status became share_link-settable; see ADR-0105 Postscript (a)).
 *
 * Retired statuses are never offered as targets (ceiling [] excludes
 * them anyway; filtered here too so no caller renders a dead option).
 */
export function legalObservationTargets(recipe, from, actor) {
    const live = recipe.observationStatuses.filter((s) => !s.retired);
    const byCeiling = live.filter((s) => s.settableBy.includes(actor));
    if (!isObservationGraphEnabled(recipe))
        return byCeiling;
    const origin = from === null ? undefined : recipe.observationStatuses.find((s) => s.key === from);
    if (origin === undefined || origin.retired)
        return byCeiling;
    return origin.transitionsTo
        .filter((t) => t.actors.includes(actor))
        .map((t) => live.find((s) => s.key === t.to))
        .filter((s) => s !== undefined)
        .filter((s) => s.settableBy.includes(actor));
}
/**
 * True when a status was reached through a share-link edge — the
 * idempotency question `/public/b/:token/.../attend` asks when a
 * double-tap (or a retried request) finds the finding already moved:
 * "is where it sits a place MY action put it" is a different question
 * from "can I still act", and conflating them turned the old guard's
 * no-target case into a hard 409 on a legitimate retry.
 */
export function isShareLinkReachableStatus(recipe, status) {
    return recipe.observationStatuses.some((s) => s.transitionsTo.some((t) => t.to === status && t.actors.includes("share_link")));
}
/**
 * ADR-0134 §3 — enforce a destination's `requiresEvidence`. The engine
 * cannot see the evidence itself (it rides the request), so the route
 * that carries it passes how many items it is attaching to the
 * milestone. `validateObservationTransition` calls this itself (from its
 * `evidenceCount` input, after the edge and note checks), so every
 * transition is fail-closed; it is exported for callers that need the
 * same rule outside a transition.
 *
 * Throws ValidationError OBSERVATION_EVIDENCE_REQUIRED when the target
 * declares `requiresEvidence` and `evidenceCount` is below 1.
 */
export function assertEvidenceRequirement(target, evidenceCount) {
    if (!target.requiresEvidence)
        return;
    if (evidenceCount !== undefined && evidenceCount >= 1)
        return;
    throw new ValidationError({
        code: "OBSERVATION_EVIDENCE_REQUIRED",
        message: `Evidence is required to move an observation to "${target.key}"`,
        details: { evidence: [`status "${target.key}" requires at least one photo`] },
    });
}
/**
 * Whether a move by `actor` out of `from` belongs to
 * POST /findings/:id/conformity and must therefore be refused by
 * POST /findings/:id/observation-status (CONFORMITY_ROUTE_REQUIRED).
 *
 * ADR-0141 reformulated the rule: the conformity route is the receiving
 * party's ONE door — it owns every response of a closure lane, which
 * concretely means three things:
 *
 *   1. The verdict on the closure origin (`observationClosure.fromStatus`
 *      — conforme stamps the overlay, no conforme walks the reopen edge).
 *   2. The response to a rejection: every move out of an
 *      awaiting_receiver_on_rejection status (postventa's "rechazada" —
 *      «Estoy de acuerdo» / «Pedir segunda revisión», the acepta_rechazo
 *      and apela_rechazo verdicts).
 *   3. For a closure lane with NO ordinary edge anywhere else in the
 *      graph, every move that lane could attempt — such a lane exists in
 *      the thread only to respond (postventa's propietario), so it has
 *      no second door even through the ceiling fallback.
 *
 * "Ordinary edges" are edges out of LIVE statuses other than `fromStatus`
 * and other than the awaiting_receiver_on_rejection statuses — those two
 * families belong to the verdict by (1) and (2), so owning only them
 * still reads as edgeless. A closure lane that ALSO owns ordinary edges
 * (facility's supervisor assigns and anula; its jefe de tienda resolves,
 * ADR-0134) keeps those on the status route. Lanes outside `closureLanes`
 * are never refused here; the graph decides for them.
 *
 * The other half of the out-of-graph guard (a closure lane jumping to
 * `fromStatus` from a NULL/retired/unknown origin through the ceiling
 * fallback) lives in `validateObservationTransition`, because it is a
 * property of the transition, not of which route carries it.
 */
export function conformityRouteOwns(recipe, actor, from) {
    if (!closureLanes(recipe).includes(actor))
        return false;
    const closureFrom = recipe.observationClosure?.fromStatus;
    if (from !== null && from === closureFrom)
        return true;
    const rejectionWait = observationPhaseKeys(recipe).awaiting_receiver_on_rejection;
    if (from !== null && rejectionWait.includes(from))
        return true;
    const hasOwnEdge = recipe.observationStatuses.some((s) => !s.retired &&
        s.key !== closureFrom &&
        !rejectionWait.includes(s.key) &&
        s.transitionsTo.some((t) => t.actors.includes(actor)));
    return !hasOwnEdge;
}
/**
 * ADR-0134 §5 — whether `lane`'s "No conforme" owes the full constancia
 * (at least one photo AND the voice note's transcript). Reads
 * `observationClosure.notConformEvidenceRequiredFor`; absent, every
 * closure lane owes it (the pre-ADR-0134 behavior).
 */
export function notConformEvidenceRequired(recipe, lane) {
    const required = recipe.observationClosure?.notConformEvidenceRequiredFor ?? closureLanes(recipe);
    return required.includes(lane);
}
/**
 * Enforce the "No conforme" constancia for `lane`. A lane that owes the
 * full constancia needs ≥1 photo and a non-blank transcript (the
 * written note adds, never substitutes). Any other lane needs a motivo —
 * a transcript or a written note — and photos are optional: a reopen
 * with no instruction leaves the assignee nothing to fix (ADR-0134 §5).
 *
 * Throws ValidationError NOT_CONFORM_EVIDENCE_REQUIRED or
 * NOT_CONFORM_REASON_REQUIRED.
 */
export function assertNotConformConstancia(recipe, lane, input) {
    const filled = (v) => typeof v === "string" && v.trim().length > 0;
    if (notConformEvidenceRequired(recipe, lane)) {
        if (input.photoCount >= 1 && filled(input.transcript))
            return;
        throw new ValidationError({
            code: "NOT_CONFORM_EVIDENCE_REQUIRED",
            message: "A non-conformity from this lane requires at least one photo and a voice note",
            details: {
                ...(input.photoCount >= 1 ? {} : { photoUrls: ["at least one photo is required"] }),
                ...(filled(input.transcript) ? {} : { transcript: ["a voice note is required"] }),
            },
        });
    }
    if (filled(input.transcript) || filled(input.note))
        return;
    throw new ValidationError({
        code: "NOT_CONFORM_REASON_REQUIRED",
        message: "A non-conformity requires a reason",
        details: { note: ["a reason (voice note or written note) is required"] },
    });
}
/**
 * ADR-0134 §6 — the default plazo (calendar days) for a GROUP of
 * observations being assigned: the most urgent one sets the deadline.
 *
 * When the recipe declares `observationDueDays`, each row's own severity
 * key is looked up there and the SMALLEST matching day count wins (the
 * most urgent). When it declares none — or no row's key is in the map —
 * this is `defaultPlazoDiasFor` over the effective S-codes verbatim, so
 * postventa resolves to exactly the days it always did.
 */
export function observationDueDaysFor(recipe, rows) {
    const list = [...rows];
    const table = recipe.observationDueDays;
    if (table !== undefined) {
        let best = null;
        for (const r of list) {
            const days = Object.prototype.hasOwnProperty.call(table, r.severity)
                ? table[r.severity]
                : undefined;
            if (days !== undefined && (best === null || days < best))
                best = days;
        }
        if (best !== null)
            return best;
    }
    return defaultPlazoDiasFor(list.map((r) => r.effectiveSeverity));
}
/**
 * El nombre visible de un estado para una audiencia. Punto único: todo
 * cliente que pinte un badge de estado pasa por acá, en vez de leer
 * `status.label` a secas y perderse el override, o —peor— de tener su
 * propio mapa por rol.
 *
 * Un actor sin override cae al `label` de siempre, así que las
 * verticales que no declaran `labelByActor` no cambian en nada y el
 * llamador puede invocar esto siempre, sin ramificar.
 *
 * `actor` es nullable porque no toda superficie tiene uno resuelto (el
 * feed del dashboard pinta la voz de la operación); sin actor, el label
 * base es la respuesta correcta.
 */
export function observationStatusLabel(recipe, status, actor) {
    const node = recipe.observationStatuses.find((s) => s.key === status);
    if (!node)
        return null;
    if (!actor)
        return node.label;
    return node.labelByActor?.[actor] ?? node.label;
}
/**
 * Closed = the closure overlay is stamped (ADR-0105 Decision 4 — the
 * three nullable columns land with the wave-2 taxonomy migration; until
 * then callers pass null), OR the status is live and terminal by
 * absence of edges in a graph-enabled vocabulary (postventa's
 * "rechazada" — ADR-0105 Postscript (c)). Retired statuses are legacy
 * open work, never closed.
 *
 * Wave-2 (ADR-0129): once `cod_procedencia` exists, PR-04 ("En
 * análisis") must additionally veto closure here — an observation whose
 * procedencia is still being resolved is not closed no matter what the
 * graph says.
 */
export function isObservationClosed(recipe, finding) {
    // Veto de procedencia (ADR-0105 Postscript (c), spec: «PR-04 "En
    // análisis" no es un cierre válido»): mientras la inmobiliaria no
    // resuelva la procedencia, la observación no lee como cerrada, ni
    // siquiera con el overlay estampado o un estado terminal-por-grafo.
    // El literal es una constante de la spec (como 'S1'..'S4'), no una
    // rama de vertical.
    if (finding.originCode === "PR-04")
        return false;
    if (finding.observationClosedAt != null)
        return true;
    if (!isObservationGraphEnabled(recipe))
        return false;
    if (finding.observationStatus === null)
        return false;
    const entry = recipe.observationStatuses.find((s) => s.key === finding.observationStatus);
    if (!entry || entry.retired)
        return false;
    return entry.transitionsTo.length === 0;
}
export const OBSERVATION_PHASES = [
    "triage",
    "awaiting_assignment",
    "with_assignee",
    "awaiting_receiver",
    "awaiting_receiver_on_rejection",
    "closed",
];
/** A status the assignee acts on through their share link — the node
 *  that owns a `share_link` edge. The same predicate `/findings/:id/assign`
 *  resolves its destination with. */
function ownsShareLinkEdge(status) {
    return status.transitionsTo.some((t) => t.actors.includes("share_link"));
}
/**
 * The status keys of each phase, for SQL predicates (core-api's rollups)
 * and for clients that bucket a list without calling `observationPhase`
 * per row. Rules, in precedence order (a key lands in the first bucket
 * that claims it):
 *
 *   closed              — live status with no outgoing edge in a
 *                         graph-enabled vocabulary (terminal by absence
 *                         of edges, ADR-0105 Postscript (c)). The closure
 *                         OVERLAY is per-row, not per-key: a predicate
 *                         built from these keys must ALSO treat
 *                         `observation_closed_at IS NOT NULL` as closed.
 *   with_assignee       — status that owns a `share_link` edge: the work
 *                         sits with the assignee (postventa "asignada").
 *   awaiting_receiver   — `observationClosure.fromStatus`: the receiving
 *                         party's verdict is pending (postventa
 *                         "resuelta").
 *   awaiting_assignment — status from which `assigning_party` reaches a
 *                         with_assignee status by a direct edge
 *                         (postventa "aceptada").
 *   awaiting_receiver_on_rejection — status whose EVERY outgoing edge is
 *                         drivable only by closure lanes (`closureLanes`):
 *                         the rejection was communicated and only the
 *                         receiving party can move it (postventa
 *                         "rechazada" since ADR-0141 — the owner accepts
 *                         it or appeals). Claimed after awaiting_assignment
 *                         on purpose: facility's "aceptada" also has only
 *                         closure-lane-drivable edges (its closers are
 *                         asset_party AND assigning_party, ADR-0134) but
 *                         it is an assignment lane first.
 *   triage              — every other live status: the entry lane where
 *                         staff accept or reject. In postventa that is
 *                         "abierta" alone. In a flat vocabulary (no
 *                         edges) every live status is triage except the
 *                         closure origin, if declared. A graph with no
 *                         triage (facility, ADR-0134) leaves this empty.
 *
 * A NULL status (no lifecycle entry yet) and retired/unknown keys are
 * never listed; `observationPhase` puts them in the ENTRY phase
 * (`observationEntryPhase`): triage when the graph has one, else
 * awaiting_assignment. A SQL mirror must do the same.
 *
 * Retired keys never appear: they are legacy rows, not phases.
 */
export function observationPhaseKeys(vocab) {
    const live = vocab.observationStatuses.filter((s) => !s.retired);
    const graph = isObservationGraphEnabled(vocab);
    const claimed = new Set();
    const claim = (keys) => {
        const out = keys.filter((k) => !claimed.has(k));
        for (const k of out)
            claimed.add(k);
        return out;
    };
    const closed = claim(graph ? live.filter((s) => s.transitionsTo.length === 0).map((s) => s.key) : []);
    const withAssignee = claim(live.filter(ownsShareLinkEdge).map((s) => s.key));
    const closureFrom = vocab.observationClosure?.fromStatus;
    const awaitingReceiver = claim(closureFrom !== undefined && live.some((s) => s.key === closureFrom) ? [closureFrom] : []);
    const awaitingAssignment = claim(live
        .filter((s) => s.transitionsTo.some((t) => t.actors.includes("assigning_party") && withAssignee.includes(t.to)))
        .map((s) => s.key));
    const lanes = closureLanes(vocab);
    const awaitingReceiverOnRejection = claim(lanes.length === 0
        ? []
        : live
            .filter((s) => s.transitionsTo.length > 0 &&
            s.transitionsTo.every((t) => t.actors.every((a) => lanes.includes(a))))
            .map((s) => s.key));
    const triage = claim(live.map((s) => s.key));
    return {
        triage,
        awaiting_assignment: awaitingAssignment,
        with_assignee: withAssignee,
        awaiting_receiver: awaitingReceiver,
        awaiting_receiver_on_rejection: awaitingReceiverOnRejection,
        closed,
    };
}
/**
 * ADR-0141 — the statuses that MEAN "the rejection was appealed": the
 * non-closed targets of the edges leaving an awaiting_receiver_on_rejection
 * status (postventa: "rechazo_apelado"). They sit in the triage phase —
 * the jefe resolves them — and `observationPhase` raises its `appealed`
 * modifier on them, in the same derived-modifier pattern as `reopened`
 * (ADR-0131). Derived from graph shape, never from a key literal.
 */
/**
 * ADR-0141 §2 — one appeal per observation CYCLE. The rule lives here,
 * in the engine, not in a route or a client: the route counts the
 * `rejection_appealed` events of the current cycle (the engine cannot
 * see `finding_events`) and this is the ceiling it compares against. A
 * second rejection after an upheld appeal plus a reopen starts a new
 * cycle, so the appeal becomes available again. Deliberately a constant,
 * and deliberately revisable: if a vertical ever needs to configure it,
 * this becomes a recipe key (e.g. `maxRejectionAppeals`) without
 * changing the model.
 */
export const MAX_REJECTION_APPEALS_PER_CYCLE = 1;
export function appealedObservationKeys(vocab) {
    const keys = observationPhaseKeys(vocab);
    const sources = new Set(keys.awaiting_receiver_on_rejection);
    if (sources.size === 0)
        return [];
    const closed = new Set(keys.closed);
    const out = new Set();
    for (const s of vocab.observationStatuses) {
        if (!sources.has(s.key))
            continue;
        for (const t of s.transitionsTo) {
            if (!closed.has(t.to))
                out.add(t.to);
        }
    }
    return [...out];
}
/**
 * El estado con que NACE una observación: la primera clave viva de la
 * fase de entrada (`observationEntryPhase`) — triage si el grafo la
 * tiene (postventa: "abierta"), si no awaiting_assignment (facility,
 * ADR-0134: "aceptada"). Se deriva del grafo, igual que la fase, para
 * que ningún carril de creación escriba el literal.
 *
 * Nacer en NULL no es neutro: NULL entra al grafo por el carril de
 * respaldo (sólo techo de nodo), así que una fila recién creada podía
 * saltar directo a un estado que sus aristas nunca permitirían. Nacer
 * en la entrada deja que las aristas gobiernen desde el primer hito.
 *
 * `null` cuando la receta no declara ciclo de observación (residential;
 * facility hasta que su receta lo declare en la Fase 2): esas filas
 * siguen naciendo sin estado, como siempre.
 */
export function observationEntryStatus(vocab) {
    if (vocab.observationStatuses.length === 0)
        return null;
    const keys = observationPhaseKeys(vocab);
    return keys[observationEntryPhase(keys)][0] ?? null;
}
/**
 * The phase a new (or reopened) observation enters: `triage` when the
 * graph has a triage lane (postventa: "abierta"), else
 * `awaiting_assignment` when it has one (ADR-0134: facility has no
 * triage — observations are born "aceptada", and a No conforme returns
 * there). A vocabulary with neither keeps `triage`, the historical
 * answer, so flat and empty vocabularies do not move.
 *
 * Exported so the SQL mirror (core-api `observation-phase-sql.ts`) maps
 * NULL/unknown statuses and `reopened` exactly as `observationPhase`.
 */
export function observationEntryPhase(keys) {
    if (keys.triage.length === 0 && keys.awaiting_assignment.length > 0) {
        return "awaiting_assignment";
    }
    return "triage";
}
/**
 * Where one row sits. `closed` is `isObservationClosed` verbatim (overlay
 * OR terminal-by-absence-of-edges), so the two never disagree; the open
 * phases come from `observationPhaseKeys`. A NULL, retired or unknown
 * status sits in the ENTRY phase (see `observationEntryPhase`): triage when the
 * graph has one, which is every vocabulary but a no-triage graph like
 * facility's (ADR-0134), where it is awaiting_assignment. `reopened` is
 * the "No conforme" reading: back at the entry phase after a cycle — it
 * is NOT a status, and this is the only place that reads
 * `observationCycle` against a number (`reopenCount` derives from the
 * same read). ADR-0131 defined it as `triage && cycle > 1`; reading the
 * entry phase instead is identical wherever triage exists, and is what
 * lets a facility row reopened to "aceptada" count as reopened.
 */
export function observationPhase(vocab, finding) {
    const closed = isObservationClosed(vocab, finding);
    const keys = observationPhaseKeys(vocab);
    const entryPhase = observationEntryPhase(keys);
    const phase = closed ? "closed" : openPhaseOf(keys, entryPhase, finding.observationStatus);
    return {
        phase,
        reopened: phase === entryPhase && (finding.observationCycle ?? 1) > 1,
        reopenCount: Math.max(0, (finding.observationCycle ?? 1) - 1),
        terminal: phase === "closed",
        appealed: !closed &&
            finding.observationStatus !== null &&
            appealedObservationKeys(vocab).includes(finding.observationStatus),
    };
}
function openPhaseOf(keys, entryPhase, status) {
    if (status === null)
        return entryPhase;
    if (keys.with_assignee.includes(status))
        return "with_assignee";
    if (keys.awaiting_receiver.includes(status))
        return "awaiting_receiver";
    if (keys.awaiting_assignment.includes(status))
        return "awaiting_assignment";
    if (keys.awaiting_receiver_on_rejection.includes(status))
        return "awaiting_receiver_on_rejection";
    if (keys.triage.includes(status))
        return "triage";
    return entryPhase;
}
/**
 * The S-code a row DISPLAYS as: the derived one when the classification
 * produced it, else the recipe's `legacyDisplayAs` for the row's badge,
 * else nothing. Read-only by contract — `derived_severity` has exactly
 * one writer (`deriveObservationSeverity`, from the booleans) and this
 * function must never feed a write (fitness:
 * effective-severity-never-persisted).
 */
export function effectiveObservationSeverity(recipe, finding) {
    const derived = finding.derivedSeverity;
    if (derived !== null && DERIVED_SEVERITIES.includes(derived)) {
        return { code: derived, origin: "derivada" };
    }
    const legacy = recipe.severities.find((s) => s.key === finding.severity)?.legacyDisplayAs;
    if (legacy !== undefined)
        return { code: legacy, origin: "legado" };
    return { code: null, origin: null };
}
//# sourceMappingURL=observation-lifecycle.js.map